/**
 * ProductChannelsTab V2
 *
 * Tab für Channel Packages.
 * Zeigt alle verknüpften Channels mit Status und Regenerate-Funktion.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useCallback } from 'react';
import { LayoutGrid, List } from 'lucide-react';
import {
  useProductChannelPackages,
  useRegenerateAllChannelPackages,
} from '@/hooks/useChannelPackage';
import { useProductReadiness } from '@/hooks/useChannelReadiness';
import {
  ChannelPackageGrid,
  ChannelPackagePreviewModal,
  ChannelPackageEditModal,
} from '@/components/channel-packages';
import { VersionHistoryOffcanvas } from '@/components/versioning';
import type { ChannelPackage } from '@/types/channel-packages';
import type { BlockType } from '@/types/content-blocks';
import { Button } from '@/components/v2/ui';
import { regenerateChannelPackage } from '@/lib/api/channel-packages';

interface ProductChannelsTabProps {
  productId: string;
  contentUpdatedAt?: string;
  /** Optional: Callback für globalen Offcanvas */
  onOpenVersionHistory?: (
    entityType: 'content_block' | 'channel_package' | 'product_asset_link',
    entityId: string,
    blockType?: BlockType,
    channelId?: string
  ) => void;
}

export function ProductChannelsTab({
  productId,
  contentUpdatedAt,
  onOpenVersionHistory,
}: ProductChannelsTabProps) {
  // State
  const [selectedPackage, setSelectedPackage] = useState<ChannelPackage | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [regeneratingIds, setRegeneratingIds] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Queries & Mutations
  const packagesQuery = useProductChannelPackages(productId);
  const regenerateAllMutation = useRegenerateAllChannelPackages(productId);
  const readinessScores = useProductReadiness(productId);

  // Build readiness map by channel_id
  const readinessMap = new Map<string, { readiness_score: number; image_rule_score: number; text_rule_score: number; issue_count: number }>();
  if (readinessScores.data) {
    for (const s of readinessScores.data) {
      readinessMap.set(s.channel_id, {
        readiness_score: s.readiness_score,
        image_rule_score: s.image_rule_score,
        text_rule_score: s.text_rule_score,
        issue_count: s.issue_count,
      });
    }
  }

  // Handler: Regenerate single package
  const handleRegenerate = useCallback((pkg: ChannelPackage) => {
    setRegeneratingIds((prev) => new Set(prev).add(pkg.id));

    // Use direct API call since we need dynamic channel_id
    const channelId = pkg.channel_id;
    regenerateChannelPackage(channelId, pkg.id).finally(() => {
      setRegeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(pkg.id);
        return next;
      });
    });
  }, []);

  // Handler: Regenerate all
  const handleRegenerateAll = useCallback(() => {
    regenerateAllMutation.mutate(undefined);
  }, [regenerateAllMutation]);

  // Handler: View versions
  const handleViewVersions = useCallback(
    (pkg: ChannelPackage) => {
      if (onOpenVersionHistory) {
        onOpenVersionHistory('channel_package', pkg.id, undefined, pkg.channel_id);
      } else {
        setSelectedPackage(pkg);
        setIsHistoryOpen(true);
      }
    },
    [onOpenVersionHistory]
  );

  // Handler: Preview
  const handlePreview = useCallback((pkg: ChannelPackage) => {
    setSelectedPackage(pkg);
    setIsPreviewOpen(true);
  }, []);

  // Handler: Edit
  const handleEdit = useCallback((pkg: ChannelPackage) => {
    setSelectedPackage(pkg);
    setIsEditOpen(true);
  }, []);

  return (
    <div className="product-channels-tab space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h5 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
            Channel Packages
          </h5>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Kanalspezifische Produkt-Exports
          </p>
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-1 rounded-lg border border-neutral-200 dark:border-neutral-800 p-1">
          <Button
            variant={viewMode === 'grid' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Channel Package Grid */}
      <ChannelPackageGrid
        packages={packagesQuery.data?.channel_packages || []}
        contentUpdatedAt={contentUpdatedAt}
        onPreview={handlePreview}
        onEdit={handleEdit}
        onRegenerate={handleRegenerate}
        onRegenerateAll={handleRegenerateAll}
        onViewVersions={handleViewVersions}
        regeneratingIds={regeneratingIds}
        isRegeneratingAll={regenerateAllMutation.isPending}
        viewMode={viewMode}
        isLoading={packagesQuery.isLoading}
        readinessMap={readinessMap}
      />

      {/* Version History Offcanvas - nur wenn kein globaler Callback */}
      {!onOpenVersionHistory && selectedPackage && (
        <VersionHistoryOffcanvas
          entityType="channel_package"
          entityId={selectedPackage.id}
          channelId={selectedPackage.channel_id}
          isOpen={isHistoryOpen}
          onClose={() => {
            setIsHistoryOpen(false);
            setSelectedPackage(null);
          }}
        />
      )}

      {/* Preview Modal */}
      <ChannelPackagePreviewModal
        package_={selectedPackage}
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setSelectedPackage(null);
        }}
      />

      {/* Edit Modal */}
      <ChannelPackageEditModal
        package_={selectedPackage}
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedPackage(null);
        }}
      />
    </div>
  );
}
